import { useEffect, useState } from 'react';
import axios from 'axios';
import utils from '../../utils/utils';
import useDatasetGrid from '../useDatasetGrid';
import useConsole from '../Console/useConsole';
import MODULE_SUB_MODULE_OPERATIONS_TREE from '../../const/ModuleSubModuleOperationsTree';
import useProject from '../Project/useProject';
import ApiEndpoints from '../../const/ApiEndpoints';
import { SwalToast } from '../../components/Common';
import useDataIngestion from '../DM/useDataIngestion';
import useModelMetaData from '../useModelMetaData';
import { DATASET_REQUIRED_FIELDS } from '../../const/CommonConst';

const BASE_URL = ApiEndpoints.MODULE_BASE_URL.EDA;
const totalPageCount = 'x-total-count';

/**
 * @function useTreatingOutliers
 * @description contains all the API calls, functions and common
 * state data for Treating Outliers submodule of  EDA module
 * @param {function} fetchModuleTasks - API call for fetching Treating Outliers module's all tasks
 * @param {array} datasetList - list of all datasets
 * @param {function} fetchDatasetList - API call for fetching list of all datasets
 * @param {function} fetchDatasetRowsWithPagination - API call for fetching dataset rows with pagination
 * @param {array} datasetRowsWithPagination - list of dataset rows with pagination
 * @param {function} setDatasetRowsWithPagination - set the list of dataset rows with pagination
 * @param {number} totalDatasetRows - total number of dataset rows
 * @param {number} datasetRowsPageCount - total number of dataset rows page count
 * @param {boolean} isLoading - value to determine when to show and hide loader.
 * @param {function} updateDatasetCells - API call for updating dataset cells
 * @param {object} project - project details
 * @returns {object} -returning multiple methods and states for Treating Outliers submodule
 * @example const {fetchModuleTasks,fetchDatasetRowsWithPagination,datasetList}
 * =useTreatingOutliers()
 */

export default function useTreatingOutliers() {
  const {
    fetchModuleTasks,
    datasetList,
    fetchDatasetList,
    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,

    updateDatasetCells,
    project,
  } = useProject();

  const {
    renameColumn,
    deleteColumn,
    duplicateColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    tasks: gridTasks,
    taskCount: gridTaskCount,
    fetchTasks: fetchGridTasks,
    terminateTask: terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    gridOperationRerun,
  } = useDatasetGrid();

  const {
    consoleModuleTasks,
    setConsoleModuleTasks,
    consoleAsyncTaskResults,
    fetchAsyncResultConsole,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    setConsoleAsyncTaskResults,
    fetchAllModels,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    fetchConsoleFileList,
  } = useConsole();
  const { markDatasetAsReadyToUse } = useDataIngestion();

  const { addResultToModelMetaData, fetchModelMetaDataSchema } =
    useModelMetaData();

  // For Active Dataset
  const [activeDataset, setActiveDataset] = useState('');

  // For Column Details of Active Dataset
  const [datasetColumnDetails, setDatasetColumnDetails] = useState([]);

  // Treating Outliers Tasks
  const [moduleTasks, setModuleTasks] = useState([]);
  const [totalModuleTask, setTotalModuleTask] = useState(0);
  const [totalModuleTaskConsole, setTotalModuleTaskConsole] = useState(0);
  const [taskCount, setTaskCount] = useState({});

  // For Response Data : Graphs or Tables or Table ID
  const [taskDetails, updateTaskDetails] = useState({});

  // storing idx
  const [idx, updateIdx] = useState([]);
  useEffect(() => {
    if (activeDataset?.id) {
      datasetList.forEach((data) => {
        if (data.id === activeDataset.id) {
          setActiveDataset(data);
        }
      });
    }
  }, [datasetList]);

  const fetchAsyncResultSplit = (taskID, type) =>
    axios
      .get(`${BASE_URL + ApiEndpoints.EDA.GET_EDA_TASKS}/${taskID}/${type}/`)
      .then((res) => {
        return res;
      });

  // For Active Operation
  const [activeOperation, setActiveOperation] = useState(
    MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.subModules.TO.operations.OD
  );

  /**
	@function resetTaskDetails
	@description used for updating the state taskDetails
	@returns {undefined}
	@example resetTaskDetails()
	*/

  // reset data on Operation and Dataset Change
  const resetTaskDetails = () => {
    updateTaskDetails({});
  };

  /**
   * @function fetchDatasetColumnDetails
   * @description API call for fetching dataset column details
   * @param {object} datasetName - contain data like table name and project id
   * @returns {Promise} Axios Promise and update the state of DatasetColumnDetails
   * @example fetchDatasetColumnDetails(datasetName);
   */

  // For fetching Active Dataset Columns
  const fetchDatasetColumnDetails = (datasetName) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.EDA +
          ApiEndpoints.EDA.EDA_GET_COLUMN_DETAILS
        }`,
        datasetName
      )
      .then(
        (res) => {
          setDatasetColumnDetails(res.data.columns);
        },
        () => {}
      );

  /**
   * @function fetchPSModuleTasks
   * @description API call for fetching Treating Outliers module's all tasks
   * @param {number} projectId - project id for the selected project
   * @param {boolean} hideLoaderOnRequest - value to determine when to show and hide loader.
   * @returns {undefined}
   * @example fetchPSModuleTasks(projectId,hideLoaderOnRequest);
   */

  // For Fetching the Tasks List
  const fetchPSModuleTasks = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      return fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.subModules.TO.key,
        'GET_EDA_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        pageIndex,
        pageSize,
        taskStatus,
        false,
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.key
      ).then((res) => {
        if (res.data) {
          setTotalModuleTask(res.headers?.[totalPageCount]);
          if (pageIndex > 1) {
            setModuleTasks((prevModuleTasks) => [
              ...prevModuleTasks,
              ...(res?.data?.tasks || []),
            ]);
          } else {
            setModuleTasks([...res.data.tasks]);
          }
        }
        return res;
      });
    }
  };
  /**
   * Fetches the count of module tasks.
   *
   * @param {string} projectId - The ID of the project.
   * @param {boolean} hideLoaderOnRequest - Whether to hide the loader on request.
   * @param {number} count - The count of tasks to fetch.
   * @return {Promise} A promise that resolves with the count of tasks.
   */
  const fetchModuleTasksCount = (projectId, hideLoaderOnRequest, count) => {
    if (projectId) {
      fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.subModules.TO.key,
        'GET_EDA_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        null,
        null,
        null,
        count,
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.key
      ).then((res) => {
        setTaskCount(res?.data);
      });
    }
  };

  /**
   * @function fetchPSModuleTasksConsole
   * @description API call for fetching data quality module tasks
   * @param {number} projectId - module tasks of a particular project id
   * @param {boolean} hideLoaderOnRequest -hiding loader on request
   * @returns {undefined}
   * @example fetchPSModuleTasksConsole(projectId,hideLoaderOnRequest);
   */
  const fetchPSModuleTasksConsole = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      fetchModuleTasksConsole(
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.subModules.TO.key,
        'GET_EDA_TASKS',
        projectId,
        hideLoaderOnRequest,
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        if (res.data) {
          setTotalModuleTaskConsole(res.headers[totalPageCount]);
          if (pageIndex > 1) {
            setConsoleModuleTasks([...consoleModuleTasks, ...res.data]);
          } else {
            setConsoleModuleTasks([...res.data]);
          }
        }
      });
    }
  };

  /**
   * @function fetchAsyncResultConsoleAndActivateDataset
   * @description API call for fetching asynchronous result
   * @param {number}  taskID - task id
   * @param {object}  additionTaskDetails - addition task details
   * @returns  {Promise} AxiosPromise
   * @example  fetchAsyncResultConsoleAndActivateDataset(taskID, additionTaskDetails)
   */
  const fetchAsyncResultConsoleAndActivateDataset = (
    taskID,
    additionTaskDetails
  ) =>
    fetchAsyncResultConsole(taskID, additionTaskDetails).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          if (utils.isNewDatasetCreatedByUdf(res.data)) {
            fetchDatasetList(
              project.activeProject?.project_id,
              true,
              [DATASET_REQUIRED_FIELDS].join()
            );
          }
          if (res.data?.parameters?.table) {
            datasetList.forEach((dataset) => {
              if (dataset.Name === res.data.parameters.table) {
                setActiveDataset({ ...dataset });
              }
            });
          }
        }
        return res;
      },
      (error) => {
        throw error;
      }
    );
  /**
   * @function fetchAsyncResult
   * @description API call for fetching detail of particular task from all the tasks
   * @param {number}  taskID - task id of the particular task
   * @param {object}  additionTaskDetails - addition information of the task
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise which update state ActiveDataset
   * @example fetchAsyncResult(taskID, additionTaskDetails, successCallback)
   */

  // Fetch Asnyc Operation Result
  const fetchAsyncResult = (taskID, additionTaskDetails, successCallback) =>
    axios.get(`${BASE_URL + ApiEndpoints.EDA.GET_EDA_TASKS}/${taskID}`).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          fetchDatasetList(
            project?.activeProject?.project_id,
            true,
            [DATASET_REQUIRED_FIELDS].join()
          );
          Object.values(
            MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.subModules.TO.operations
          )?.filter((data) => {
            if (data.key === additionTaskDetails.operation) {
              if (res.data?.parameters?.dataset_id) {
                datasetList.forEach((dataset) => {
                  if (dataset.id === Number(res.data.parameters.dataset_id)) {
                    setActiveDataset(dataset);
                  }
                });
              }

              setActiveOperation(data);
              if (res.data?.results?.idx !== undefined) {
                updateIdx(res.data?.results?.idx);
              }
              updateTaskDetails(res.data);
              successCallback(res.data);
            }
            return null;
          });
        } else {
          updateTaskDetails({});
        }
      },
      () => {}
    );

  /**
   * @function  terminateTask
   * @description API call for deleting a particular task
   * @param {number}  taskID - task id of the task we want to delete
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @param {function} errorCallback - Callback function to execute when
   * any error happens during the calling of API
   * @returns  {Promise} AxiosPromise
   * @example  terminateTask(taskID, successCallback, errorCallback)
   */

  // For Terminating Task in Task Manager
  const terminateTask = (taskID) =>
    axios
      .delete(`${BASE_URL + ApiEndpoints.EDA.GET_EDA_TASKS}/${taskID}`)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });

  /**
   * @function ftsRerunOperation
   * @description API call for reruning the operation
   * @param {object} body - body
   * @returns {Promise} AxiosPromise
   * @example ftsRerunOperation(body);
   */
  const edaRerunOperation = (body) => {
    return axios
      .post(`${BASE_URL + ApiEndpoints.EDA.RERUN}`, body)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function getOutlierDetectionResults
   * @description used to perform detection operations
   * @param {object} body - object storing detail of the operation performed.
   * @param {function} successCallback - success callback when function
   * is executed successfully required data is sent.
   * @param {function} errorCallback - error callback when function failed required data is sent.
   * @returns {undefined}
   * @example getOutlierDetectionResults(body,successCallback,errorCallback);
   */

  // Get Detection Results
  const getOutlierDetectionResults = (body, successCallback, errorCallback) =>
    axios
      .post(
        `${BASE_URL + ApiEndpoints.EDA.EDA_GET_OUTLIER_DETECTION_RESULTS}`,
        body
      )
      .then(
        () => {
          successCallback();
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
        },
        (error) => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          errorCallback(error);
        }
      );

  /**
   * @function getOutlierTreatmentResults
   * @description used to get charts for before and after  treatment visualization.
   * @param {object} body - object storing charts of the operation performed.
   * @param {function} successCallback - success callback when function
   * is executed successfully required data is sent.
   * @param {function} errorCallback - error callback when function failed required data is sent.
   * @returns {undefined}
   * @example getOutlierTreatmentResults(body,successCallback,errorCallback);
   */

  // Get Detection Results
  const getOutlierTreatmentResults = (body, successCallback, errorCallback) =>
    axios
      .post(
        `${BASE_URL + ApiEndpoints.EDA.EDA_GET_OUTLIER_TREATMENT_RESULTS}`,
        body
      )
      .then(
        () => {
          successCallback();
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
        },
        (error) => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          errorCallback(error);
        }
      );

  /**
   * @function saveTreatmentResults
   * @description used to save the result of treatment.
   * @param {object} body - object storing detail of the operation performed.
   * @param {function} successCallback - success callback when function
   * is executed successfully required data is sent.
   * @param {function} errorCallback - error callback when function failed required data is sent.
   * @returns {undefined}
   * @example saveTreatmentResults(body,successCallback,errorCallback);
   */

  // Get Detection Results
  const saveTreatmentResults = (body, successCallback, errorCallback) =>
    axios
      .post(`${BASE_URL + ApiEndpoints.EDA.EDA_SAVE_TREATMENT_RESULTS}`, body)
      .then(
        () => {
          successCallback();
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
        },
        (error) => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          errorCallback(error);
        }
      );

  /**
	@function markTaskReadUnreadVariablAnalysis
	@description change the read status of the task in job queue section
	@param {object} taskParams - object containing detail of task like project id, section name etc
	@param {function} successCallback - callback executed when promise get resolved
	@param {function} errorCallback - callback executed when promise get failed
	@returns Axios promise
	@example markTaskReadUnreadVariablAnalysis(taskParams, successCallback, errorCallback)
	*/

  const markTaskReadUnreadTreatingOutliers = (
    taskParams,
    successCallback,
    errorCallback = () => {}
  ) =>
    axios
      .get(`${BASE_URL + ApiEndpoints.EDA.EDA_JOB_QUEUE_MARK_READ_UNREAD}`, {
        params: taskParams,
      })
      .then(
        () => {
          successCallback();
        },
        () => {
          errorCallback();
        }
      );

  const fetchTableDataResult = (tableId, pageNumber, pageSize) =>
    axios
      .get(`${BASE_URL + ApiEndpoints.EDA.EDA_TABLE_DATA}/${tableId}`, {
        params: {
          page: pageNumber + 1,
          pagesize: pageSize,
        },
      })
      .then(
        (res) => res,
        (err) => {
          throw err;
        }
      );
  const onDownloadCSVData = (data) =>
    axios.post(BASE_URL + ApiEndpoints.EDA.EDA_DOWNLOAD_TABLE, data).then(
      (response) => {
        SwalToast({
          icon: 'success',
          title:
            'Started preparing download link. Please check downloads page for further details.',
        });
        return response;
      },
      (err) => {
        throw err;
      }
    );

  return {
    activeOperation,
    setActiveOperation,

    activeDataset,
    setActiveDataset,

    datasetColumnDetails,
    fetchDatasetColumnDetails,

    moduleTasks,
    setModuleTasks,
    totalModuleTask,
    fetchPSModuleTasks,
    taskCount,
    fetchModuleTasksCount,

    resetTaskDetails,
    taskDetails,

    fetchAsyncResult,

    datasetList,
    fetchDatasetList,

    getOutlierDetectionResults,

    getOutlierTreatmentResults,

    saveTreatmentResults,
    terminateTask,

    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,

    updateDatasetCells,

    renameColumn,
    duplicateColumn,
    deleteColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    gridTasks,
    gridTaskCount,
    edaRerunOperation,
    fetchGridTasks,
    terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    idx,
    updateIdx,
    markTaskReadUnreadTreatingOutliers,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    fetchTableDataResult,
    onDownloadCSVData,
    gridOperationRerun,

    // console related
    consoleModuleTasks,
    totalModuleTaskConsole,
    consoleAsyncTaskResults,
    fetchAsyncResultConsoleAndActivateDataset,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchPSModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    fetchAllModels,
    project,
    setConsoleAsyncTaskResults,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    markDatasetAsReadyToUse,
    fetchConsoleFileList,
    addResultToModelMetaData,
    fetchModelMetaDataSchema,
    fetchAsyncResultSplit,
  };
}
